if (!customElements.get('klaviyo-step-form')) {
  class KlaviyoStepForm extends HTMLElement {
    constructor() {
      super();

      this.emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      this.phonePattern = /^[\d\s()+-]+$/;
      
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleFieldInput = this.handleFieldInput.bind(this);
      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('[data-klaviyo-email]');
      this.phoneInput = this.querySelector('[data-klaviyo-phone]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.notification = this.querySelector('[data-klaviyo-notification]');
      this.closeButton = this.querySelector('[data-klaviyo-form-close]');
      this.trigger = this.closest('.form-step')?.querySelector('[data-klaviyo-form-trigger] button');
      this.submitLabel = this.submitButton?.textContent.trim() || '';
      this.messages = {
        success: this.dataset.successMessage,
        error: this.dataset.errorMessage,
        sending: this.dataset.sendingLabel,
        missingListId: this.dataset.missingListIdMessage,
        missingCompanyId: this.dataset.missingCompanyIdMessage,
        submitError: this.dataset.submitErrorMessage,
        invalidEmail: this.dataset.invalidEmailMessage,
        invalidPhone: this.dataset.invalidPhoneMessage,
        blankEmail: this.dataset.blankEmailMessage,
        blankPhone: this.dataset.blankPhoneMessage,
        blankEmailPhone: this.dataset.blankEmailPhoneMessage,
      };

      this.setOpen(this.dataset.open === 'true', { focus: false });

      this.form?.addEventListener('submit', this.handleSubmit);
      this.emailInput?.addEventListener('input', this.handleFieldInput);
      this.emailInput?.addEventListener('blur', this.handleFieldInput);
      this.phoneInput?.addEventListener('input', this.handleFieldInput);
      this.phoneInput?.addEventListener('blur', this.handleFieldInput);
      this.trigger?.addEventListener('click', this.open);
      this.closeButton?.addEventListener('click', this.close);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.emailInput?.removeEventListener('input', this.handleFieldInput);
      this.emailInput?.removeEventListener('blur', this.handleFieldInput);
      this.phoneInput?.removeEventListener('input', this.handleFieldInput);
      this.phoneInput?.removeEventListener('blur', this.handleFieldInput);
      this.trigger?.removeEventListener('click', this.open);
      this.closeButton?.removeEventListener('click', this.close);
    }

    open() {
      this.setOpen(true);
    }

    close() {
      this.setOpen(false);
      this.trigger?.focus();
    }

    setOpen(isOpen, options = {}) {
      this.dataset.open = isOpen ? 'true' : 'false';
      this.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      this.trigger?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      if ('inert' in this) {
        this.inert = !isOpen;
      }

      if (isOpen && options.focus !== false) {
        window.requestAnimationFrame(() => this.emailInput?.focus());
      }
    }

    trackLeadEvent() {
      if (typeof window.fbq !== 'function') return;

      const email = this.emailInput?.value?.trim().toLowerCase();
      const phone = this.normalizePhone(this.phoneInput?.value);

      window.fbq('track', 'Lead', {
        content_name: 'Klaviyo Step Form',
        content_category: 'Form',
        ...(email && { em: email }),
        ...(phone && { ph: phone }),
      });
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (!this.validate()) return;

      this.setLoading(true);

      try {
        await this.submitToKlaviyo();
        this.trackLeadEvent();
        this.showNotification(this.messages.success, 'success');
        this.form.reset();
        this.clearFieldState(this.emailInput);
        this.clearFieldState(this.phoneInput);

        if (this.dataset.swapGroup) {
          this.dispatchEvent(new CustomEvent('klaviyo:subscribed', {
            bubbles: true,
            detail: { group: this.dataset.swapGroup },
          }));
        }
      } catch (error) {
        this.showNotification(error.message || this.messages.error, 'error');
      } finally {
        this.setLoading(false);
      }
    }

    handleFieldInput(event) {
      this.validateField(event.currentTarget, { showMessage: false });
    }

    validate() {
      const blankFields = [this.phoneInput, this.emailInput].filter((field) => field && !field.value.trim());

      if (blankFields.length > 0) {
        blankFields.forEach((field) => this.setFieldError(field));
        blankFields[0].focus();
        this.showNotification(this.getBlankFieldsMessage(blankFields), 'error');

        return false;
      }

      if (!this.validateField(this.emailInput, { focus: true, showMessage: true })) {
        return false;
      }

      if (!this.validateField(this.phoneInput, { focus: true, showMessage: true })) {
        return false;
      }

      return true;
    }

    normalizePhone(value) {
      const digits = value.replace(/\D/g, '');

      if (digits.length === 10) {
        return `+1${digits}`;
      }

      if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}`;
      }

      return null;
    }

    getBlankFieldsMessage(blankFields) {
      const hasBlankEmail = blankFields.includes(this.emailInput);
      const hasBlankPhone = blankFields.includes(this.phoneInput);

      if (hasBlankEmail && hasBlankPhone) {
        return this.messages.blankEmailPhone;
      }

      if (hasBlankEmail) {
        return this.messages.blankEmail;
      }

      return this.messages.blankPhone;
    }

    validateField(field, options = {}) {
      if (!field) return false;

      const value = field.value.trim();

      if (!value) {
        this.clearFieldState(field);
        return false;
      }

      const isEmail = field === this.emailInput;
      
      let isValid;
      if (isEmail) {
        isValid = this.emailPattern.test(value);
      } else {
        const normalized = this.normalizePhone(value);
        isValid = Boolean(normalized);
      }

      if (isValid) {
        this.setFieldSuccess(field);
        return true;
      }

      this.setFieldError(field, options);

      if (options.showMessage) {
        this.showNotification(
          isEmail ? this.messages.invalidEmail : this.messages.invalidPhone,
          'error'
        );
      }

      return false;
    }

    async submitToKlaviyo() {
      const listId = this.dataset.listId;
      const companyId = this.dataset.companyId;

      if (!listId) {
        throw new Error(this.messages.missingListId);
      }

      if (!companyId) {
        throw new Error(this.messages.missingCompanyId);
      }

      const email = this.emailInput.value.trim();
      const phoneNumber = this.normalizePhone(this.phoneInput.value);
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Klaviyo Step Form',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  phone_number: phoneNumber,
                  subscriptions: {
                    email: {
                      marketing: {
                        consent: 'SUBSCRIBED',
                      },
                    },
                    sms: {
                      marketing: {
                        consent: 'SUBSCRIBED',
                      },
                    },
                  },
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: listId,
              },
            },
          },
        },
      };

      const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: '2026-04-15',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await this.getResponseError(response));
      }

      if (window.klaviyo?.push) {
        window.klaviyo.push(['identify', {
          email,
          phone_number: phoneNumber,
        }]);
      }
    }

    async getResponseError(response) {
      try {
        const data = await response.json();
        const error = data.errors?.[0];

        return error?.detail || error?.title || this.messages.submitError;
      } catch (error) {
        return this.messages.submitError;
      }
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;

      this.submitButton.disabled = isLoading;
      this.submitButton.textContent = isLoading ? this.messages.sending || this.submitLabel : this.submitLabel;
    }

    showNotification(message, status) {
      if (!this.notification || !message) return;

      this.notification.textContent = message;
      this.notification.dataset.status = status;
      this.notification.hidden = false;
    }

    setFieldSuccess(field) {
      if (!field) return;

      const fieldWrapper = field.closest('.klaviyo-step-form__field');

      field.removeAttribute('aria-invalid');
      field.classList.remove('is-invalid');
      field.classList.add('is-valid');
      fieldWrapper?.classList.remove('is-invalid');
      fieldWrapper?.classList.add('is-valid');
    }

    setFieldError(field, options = {}) {
      if (!field) return;

      const fieldWrapper = field.closest('.klaviyo-step-form__field');

      field.setAttribute('aria-invalid', 'true');
      field.classList.remove('is-valid');
      field.classList.add('is-invalid');
      fieldWrapper?.classList.remove('is-valid');
      fieldWrapper?.classList.add('is-invalid');

      if (options.focus) {
        field.focus();
      }
    }

    clearFieldState(field) {
      if (!field) return;

      const fieldWrapper = field.closest('.klaviyo-step-form__field');

      field.removeAttribute('aria-invalid');
      field.classList.remove('is-invalid');
      field.classList.remove('is-valid');
      fieldWrapper?.classList.remove('is-invalid');
      fieldWrapper?.classList.remove('is-valid');
    }
  }

  customElements.define('klaviyo-step-form', KlaviyoStepForm);
}
